import { IToolPanelAngularComp } from '@ag-grid-community/angular'
import { GridApi, IToolPanelParams } from '@ag-grid-community/core'
import { Component, OnDestroy } from '@angular/core'
import { FormControl } from '@angular/forms'
import { FilterGroup } from '@helios/common-api'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { filter, takeUntil, tap } from 'rxjs/operators'
import {
  FilterGroupService,
  FilterGroupUpdate,
} from '../../../services/filter-group.service'
import {
  CreateNewFilterGroupModalAction,
  CreateNewFilterGroupModalService,
} from '../create-new-filter-group-modal/create-new-filter-group-modal.service'
import { DeleteFilterGroupConfirmationModalService } from '../delete-filter-group-confirmation-modal/delete-filter-group-confirmation-modal.service'
import { FilterGroupsToolPanelParams } from './filter-groups-tool-panel-sidebar-utils'

export type AgInitParams<T> = FilterGroupsToolPanelParams<T> & IToolPanelParams

@Component({
  selector: 'hs-filter-groups-tool-panel',
  templateUrl: './filter-groups-tool-panel.component.html',
  styleUrls: ['./filter-groups-tool-panel.component.scss'],
})
export class FilterGroupsToolPanelComponent<T>
  implements IToolPanelAngularComp, OnDestroy
{
  agInitParams?: AgInitParams<T>
  filterGroups?: FilterGroup[]

  search = new FormControl('')

  filteredFilterGroups?: FilterGroup[]

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly service: FilterGroupService,
    private readonly deleteFilterGroupConfirmationModalService: DeleteFilterGroupConfirmationModalService,
    private readonly createNewFilterGroupModalService: CreateNewFilterGroupModalService,
    private readonly toastService: ToastService,
  ) {}

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  async agInit(params: AgInitParams<T>): Promise<void> {
    this.agInitParams = params

    const groups = await this.service.getFilterGroups(params.gridKey)
    this.setFilterGroups(groups)
    this.search.valueChanges
      .pipe(
        takeUntil(this.destroySubject$),
        tap(() => this.filterFilterGroups()),
      )
      .subscribe()

    this.service.filterGroupAdditions
      .pipe(
        takeUntil(this.destroySubject$),
        filter((group: FilterGroup) => group.gridKey === params.gridKey),
      )
      .subscribe((filterGroup: FilterGroup) => {
        this.addFilterGroup(filterGroup)
      })
  }

  async createNewFilterGroup(): Promise<void> {
    if (this.agInitParams == null) return
    const { api, getExternalFilters, gridKey } = this.agInitParams

    const response = await this.createNewFilterGroupModalService
      .open()
      .catch(() => {
        //Do nothing
      })

    if (response == null) return

    const { name, description, action } = response
    const filters = this.getFilters(api, getExternalFilters)

    const newGroupId = await this.service.createFilterGroup(
      gridKey,
      name,
      description,
      filters,
    )

    if (action === CreateNewFilterGroupModalAction.SAVE_AND_APPLY)
      await this.applyFilterGroup(
        {
          name,
          description,
          gridKey,
          filters,
          filterGroupPrefix: newGroupId,
        },
        this.agInitParams,
      )
  }

  filterFilterGroups() {
    const searchValue: string | undefined = this.search.value
      ?.trim()
      .toLowerCase()
    if (searchValue == null || searchValue == '') {
      this.filteredFilterGroups = this.filterGroups
    } else {
      this.filteredFilterGroups = this.filterGroups?.filter(
        group =>
          group.name.toLowerCase().includes(searchValue) ||
          group.description.toLowerCase().includes(searchValue),
      )
    }

    this.filteredFilterGroups = this.filteredFilterGroups
      ?.concat()
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  updateFilters(filterGroup: FilterGroup, params: AgInitParams<T>) {
    this.updateFilters$(params, filterGroup)
  }

  private async updateFilters$(
    params: AgInitParams<T>,
    filterGroup?: FilterGroup,
  ): Promise<void> {
    if (filterGroup == null) return

    const newFilters = this.getFilters(params.api, params.getExternalFilters)

    await this.service.updateFilterGroup(filterGroup.filterGroupPrefix, {
      filters: newFilters,
    })

    this.setFilterGroups(
      this.filterGroups?.map(group => {
        if (group.filterGroupPrefix === filterGroup.filterGroupPrefix) {
          return {
            ...group,
            filters: newFilters,
          }
        }
        return group
      }),
    )
  }

  async deleteFilterGroup(filterGroup: FilterGroup, params: AgInitParams<T>) {
    try {
      await this.deleteFilterGroupConfirmationModalService.toggle(filterGroup)
    } catch {
      return //Do Nothing User Cancelled
    }

    await this.service.deleteFilterGroup(filterGroup.filterGroupPrefix)
    this.removeFilterGroup(filterGroup, params)
  }

  async cloneFilterGroup(
    filterGroupPrefix: string,
    params: AgInitParams<T>,
  ): Promise<void> {
    const response = await this.createNewFilterGroupModalService
      .open()
      .catch(() => {
        //Do nothing
      })

    if (response == null) return

    const { name, description, action } = response

    const newId = await this.service.cloneFilterGroup(
      filterGroupPrefix,
      name,
      description,
    )

    if (newId == null) return

    const newGroup = await this.service.getFilterGroup(newId)

    if (newGroup == null) return

    this.addFilterGroup(newGroup)

    if (action === CreateNewFilterGroupModalAction.SAVE_AND_APPLY)
      await this.applyFilterGroup(newGroup, params)
  }

  async applyFilterGroup(filterGroup: FilterGroup, params: AgInitParams<T>) {
    params.api.setFilterModel(filterGroup.filters.agGridFilterModel)
    params.setExternalFilters(filterGroup.filters.externalFilters as T)

    if (params.api.isToolPanelShowing()) {
      params.api.closeToolPanel()
    }
  }

  async updateFilterGroup({
    filterGroup,
    update,
  }: {
    filterGroup: FilterGroup
    update: FilterGroupUpdate
  }) {
    await this.service.updateFilterGroup(filterGroup.filterGroupPrefix, update)

    this.setFilterGroups(
      this.filterGroups?.map(group => {
        if (group.filterGroupPrefix !== filterGroup.filterGroupPrefix)
          return group
        return {
          ...group,
          ...update,
        }
      }),
    )
  }

  onCopyFilterGroupUUID(uuid: string) {
    navigator.clipboard
      .writeText(uuid)
      .then(() =>
        this.toastService.success(
          `Filter Group Id: ${uuid} copied to clipboard.`,
        ),
      )
  }

  private setFilterGroups(groups?: FilterGroup[]): void {
    this.filterGroups = groups
    this.filterFilterGroups()
  }

  private addFilterGroup(group: FilterGroup): void {
    const currentFilterGroups = this.filterGroups ?? []
    if (
      currentFilterGroups.some(
        filterGroup =>
          filterGroup.filterGroupPrefix === group.filterGroupPrefix,
      )
    )
      return

    this.setFilterGroups([...currentFilterGroups, group])
    this.filterFilterGroups()
  }

  private removeFilterGroup(
    filterGroup: FilterGroup,
    params: AgInitParams<T>,
  ): void {
    this.setFilterGroups(
      this.filterGroups?.filter(
        group => group.filterGroupPrefix !== filterGroup.filterGroupPrefix,
      ),
    )
  }

  private getFilters(api: GridApi, getExternalFilters: () => T) {
    return {
      agGridFilterModel: { ...api.getFilterModel() },
      externalFilters: { ...getExternalFilters() },
    }
  }
}
